import { isImeComposing } from './isImeComposing';

/**
 * ADM-16。ダイアログのフォーカスの閉じ込め（React に依らない DOM の処理）。useModalDialog が使い、
 * tool/dev/focus-trap-browser-check.mjs が実際のブラウザで確かめる。
 *
 * EV-123・EV-124。Tab の移動は keydown で横取りせず、すべてブラウザに任せる（日付の欄の 年・月・
 * 日、動画の中の操作、ラジオボタンの組、Chrome のスクロールできる領域を保つ）。ダイアログの最初と
 * 最後に番兵（tabindex=0・aria-hidden）を置き、フォーカスが番兵に来たら反対の端の操作へ移す。
 * フォーカスがダイアログの外へ出たら中へ戻す（EV-119）。
 */

// RV-123。操作つきの動画・音声、iframe、contenteditable、summary も、端の操作の候補に含める。
// ここは「最初・最後の操作」を探すためだけの一覧で、Tab の順番はブラウザが決める。
const FOCUSABLE = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'video[controls]',
  'audio[controls]',
  'iframe',
  '[contenteditable=""]',
  '[contenteditable="true"]',
  '[contenteditable="plaintext-only"]',
  'details > summary:first-of-type',
  '[tabindex]',
].join(', ');

const INPUTS =
  'input:not([disabled]):not([type="hidden"]):not([readonly]), select:not([disabled]), textarea:not([disabled]):not([readonly])';

const SENTINEL = 'data-focus-sentinel';

/**
 * 端の候補に含めない要素: tabindex が負、隠れている（hidden・inert・aria-hidden の中。番兵も
 * aria-hidden なので除かれる）、閉じた details の中（summary 以外）、CSS で隠れたもの（EV-122）。
 */
function isSkipped(element: HTMLElement): boolean {
  const tabindex = element.getAttribute('tabindex');
  if (tabindex !== null && Number(tabindex) < 0) return true;
  if (element.closest('[hidden], [inert], [aria-hidden="true"]')) return true;
  const details = element.parentElement?.closest('details');
  if (details && !details.open && element.tagName !== 'SUMMARY') return true;
  return isHiddenByCss(element);
}

/**
 * EV-122。CSS で隠れた要素（display:none・visibility:hidden。Tailwind の hidden クラスなど）。
 * レイアウトのある要素はすぐ返し、無いもの（jsdom ではすべて）だけ計算済みのスタイルで確かめる。
 */
function isHiddenByCss(element: HTMLElement): boolean {
  if (element.getClientRects().length > 0 || element.offsetParent !== null) {
    return window.getComputedStyle(element).visibility === 'hidden';
  }
  if (window.getComputedStyle(element).visibility === 'hidden') return true;
  for (let node: HTMLElement | null = element; node; node = node.parentElement) {
    if (window.getComputedStyle(node).display === 'none') return true;
  }
  return false;
}

/** 同じ name のラジオボタンは、ブラウザと同じく選ばれているもの（無ければ先頭）だけを端の候補にする。 */
function isOtherRadio(element: HTMLElement, all: HTMLElement[]): boolean {
  if (!(element instanceof HTMLInputElement) || element.type !== 'radio' || !element.name) return false;
  const group = all.filter(
    (other) => other instanceof HTMLInputElement && other.type === 'radio' && other.name === element.name
  ) as HTMLInputElement[];
  const stop = group.find((radio) => radio.checked) ?? group[0];
  return stop !== element;
}

/**
 * EV-127。中に操作が無く、はみ出しているスクロールできる領域。Chrome（130 以降）と Firefox は
 * tabindex が無くても Tab で止まる（キーボードでスクロールできるように）。Safari は止まらず、
 * focus() も効かない（端の候補にしても focusFirstWorking が次を試す）。
 */
function isKeyboardScroller(element: HTMLElement): boolean {
  const overflowsY = element.scrollHeight > element.clientHeight;
  const overflowsX = element.scrollWidth > element.clientWidth;
  if (!overflowsY && !overflowsX) return false;
  const style = window.getComputedStyle(element);
  const scrolls = (value: string) => value === 'auto' || value === 'scroll';
  // jsdom は overflow の一括指定を overflowX・overflowY に展開しないため、一括指定も見る。
  const y = style.overflowY || style.overflow;
  const x = style.overflowX || style.overflow;
  if (!(overflowsY && scrolls(y)) && !(overflowsX && scrolls(x))) return false;
  const inner = Array.from(element.querySelectorAll<HTMLElement>(FOCUSABLE));
  return !inner.some((child) => !isSkipped(child));
}

/**
 * 中に区切り・内部の操作を持つ要素（日付の欄の 年・月・日、動画の再生・音量など、iframe の中）。
 * フォーカスがその中のどこにあっても activeElement はその要素になるため、最初の区切りに
 * いるかを外から知ることができない。
 */
const COMPOSITE_INPUT = new Set(['date', 'time', 'datetime-local', 'month', 'week']);

function isComposite(element: HTMLElement): boolean {
  if (element instanceof HTMLInputElement) return COMPOSITE_INPUT.has(element.type);
  if (element.matches('video[controls], audio[controls]')) return true;
  return element.tagName === 'IFRAME';
}

/** 端の候補（最初・最後の操作を探すための一覧。DOM の順）。スクロールできる領域も含む（EV-127）。 */
export function focusablesIn(container: HTMLElement): HTMLElement[] {
  const all = Array.from(container.querySelectorAll<HTMLElement>('*')).filter(
    (element) => (element.matches(FOCUSABLE) || isKeyboardScroller(element)) && !isSkipped(element)
  );
  return all.filter((element) => !isOtherRadio(element, all));
}

/** 開いたとき・外から戻すときの行き先の一覧。スクロールできる領域は含めない（操作ではないため）。 */
function operationsIn(container: HTMLElement): HTMLElement[] {
  return focusablesIn(container).filter((element) => element.matches(FOCUSABLE));
}

/**
 * 一覧の start から step の向きに、実際にフォーカスが移る要素を探して移す（EV-122）。
 * focus() が効かなかった（activeElement が変わらない）ときは次を試す。
 */
function focusFirstWorking(items: HTMLElement[], start: number, step: 1 | -1): boolean {
  for (let i = 0; i < items.length; i += 1) {
    const candidate = items[(start + step * i + items.length * items.length) % items.length];
    candidate.focus();
    if (document.activeElement === candidate) return true;
  }
  return false;
}

/** 最初の操作（入力欄を優先）へ。無ければダイアログそのもの（tabIndex=-1）へ。 */
export function focusInto(container: HTMLElement, preferInput = true) {
  const input = preferInput
    ? Array.from(container.querySelectorAll<HTMLElement>(INPUTS)).find((element) => !isSkipped(element))
    : undefined;
  if (input) {
    input.focus();
    if (document.activeElement === input) return;
  }
  if (!focusFirstWorking(operationsIn(container), 0, 1)) container.focus();
}

function focusEdge(container: HTMLElement, edge: 'first' | 'last') {
  const items = focusablesIn(container);
  const moved =
    edge === 'first' ? focusFirstWorking(items, 0, 1) : focusFirstWorking(items, items.length - 1, -1);
  if (!moved) container.focus();
}

/** RV-123。Safari は操作つきの動画・音声に Tab で止まらないため、tabindex="0" を補う。 */
function ensureMediaFocusable(container: HTMLElement) {
  container.querySelectorAll<HTMLElement>('video[controls], audio[controls]').forEach((media) => {
    if (!media.hasAttribute('tabindex')) media.setAttribute('tabindex', '0');
  });
}

/**
 * EV-207。Safari の既定の設定（「Tab キーを押したときに各項目を強調表示」が切）では、tabindex の無い
 * button・リンク（a[href]）・checkbox・radio などのボタン型の input に Tab が止まらない（macOS の
 * WebKit で確認。テキストの欄・select・summary・tabindex を明示した要素には止まる。area[href] にも
 * 止まるので、飛ばされうる種類に含めない）。止まるかはページから読めない。
 */
const BUTTON_LIKE_INPUT = new Set(['checkbox', 'radio', 'button', 'submit', 'reset', 'image', 'file', 'color', 'range']);

function maySkipByTabSetting(element: HTMLElement): boolean {
  if (element.hasAttribute('tabindex')) return false;
  if (element.matches('button, a[href]')) return true;
  return element instanceof HTMLInputElement && BUTTON_LIKE_INPUT.has(element.type);
}

const VISUALLY_HIDDEN = {
  position: 'fixed',
  width: '1px',
  height: '1px',
  padding: '0',
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: '0',
};

/**
 * EV-207。element と同じ種類（Tab が止まるかの扱いが同じ）の見えない探り要素。aria-hidden で
 * 読み上げず、端の候補にも入らない（isSkipped）。
 */
function createProbe(element: HTMLElement): HTMLElement {
  const probe = document.createElement(element.tagName.toLowerCase());
  if (element instanceof HTMLInputElement) (probe as HTMLInputElement).type = element.type;
  if (probe.tagName === 'A') probe.setAttribute('href', '#');
  if (probe.tagName === 'BUTTON') probe.setAttribute('type', 'button');
  probe.setAttribute('aria-hidden', 'true');
  Object.assign(probe.style, VISUALLY_HIDDEN);
  return probe;
}

function createSentinel(position: 'start' | 'end'): HTMLSpanElement {
  const sentinel = document.createElement('span');
  sentinel.setAttribute(SENTINEL, position);
  sentinel.setAttribute('tabindex', '0');
  sentinel.setAttribute('aria-hidden', 'true');
  // 見えない（読み上げもしない）が、Tab では止まる。
  Object.assign(sentinel.style, VISUALLY_HIDDEN);
  return sentinel;
}

/**
 * EV-126。兄弟の間隔を付ける指定（Tailwind の space-x/y-*・divide-x/y-*。応答の接頭辞つきも）。
 * これらは `> :not([hidden]) ~ :not([hidden])` に余白・線を付けるため、番兵を最初の子に置くと
 * 最初の本来の子に余分な余白が付く（ログアウトの確認で 24px → 40px）。番兵に hidden を付けると
 * フォーカスできなくなるので、こうした要素では番兵を中に置かない。
 */
const SIBLING_SPACING = /(^|\s)([\w-]+:)*(space-[xy]|divide-[xy])(-|\s|$)/;

function hasSiblingSpacing(element: Element | null): boolean {
  return Boolean(element && SIBLING_SPACING.test(element.getAttribute('class') ?? ''));
}

export interface FocusTrapOptions {
  /** このダイアログが手前（最後に開いた）か。手前のものだけがキー操作とフォーカスを扱う。 */
  isActive: () => boolean;
  /** Escape で閉じる。 */
  onEscape: () => void;
}

/** container にフォーカスの閉じ込めを付ける。戻り値で外す。 */
export function installFocusTrap(container: HTMLElement, { isActive, onEscape }: FocusTrapOptions) {
  ensureMediaFocusable(container);

  const start = createSentinel('start');
  const end = createSentinel('end');
  // 番兵の置き場所。ふだんはダイアログの中の最初と最後。ダイアログが兄弟の間隔の指定を持つ
  // ときは、ダイアログの外側（直前と直後の兄弟）に置く（EV-126）。外側の親も間隔の指定を持つ
  // ときは、ダイアログの位置がずれるので中に置く（その場合の余白は残る）。
  const parent = container.parentElement;
  const outside = hasSiblingSpacing(container) && parent !== null && !hasSiblingSpacing(parent);
  // 番兵はいつも端に置き直す（React があとから子を足すと、番兵の後ろに入ることがある）。
  const place = () => {
    // EV-127。ダイアログが親から外された後（後片付けの前）に監視が走ったときは置き直さない
    // （以前は insertBefore が NotFoundError を出した）。
    if (outside && container.parentNode !== parent) return;
    if (outside && parent) {
      if (container.previousSibling !== start) parent.insertBefore(start, container);
      if (container.nextSibling !== end) parent.insertBefore(end, container.nextSibling);
    } else {
      if (container.firstChild !== start) container.insertBefore(start, container.firstChild);
      if (container.lastChild !== end) container.appendChild(end);
    }
    ensureMediaFocusable(container);
  };
  place();
  // EV-127。深い位置にあとから足した動画にも tabindex を補うため、子孫の追加も見る（subtree）。
  const observer = typeof MutationObserver === 'undefined' ? null : new MutationObserver(place);
  observer?.observe(container, { childList: true, subtree: true });
  if (outside && parent) observer?.observe(parent, { childList: true });

  // EV-127。最初の操作での Shift+Tab は、keydown の間に最後の番兵へフォーカスを移し、そこからの
  // 移動をブラウザに任せる。ブラウザは keydown の処理の後のフォーカスから前へ進むので、最後の操作の
  // 最後の区切り（日付の欄の「日」、動画の中の最後の操作、スクロールできる領域）へ入る
  // （Chromium・Firefox・WebKit で確認）。以前は最初の番兵に来てから最後の操作へ focus() で
  // 移していたため、日付の欄の先頭・動画そのものに入り、残りの区切りを飛ばした。
  let handingOff = false;
  let fallback: number | undefined;
  // EV-207。探り要素と、それぞれに対応する本物の操作。
  const probes = new Map<HTMLElement, HTMLElement>();
  const removeProbes = () => {
    probes.forEach((_, probe) => probe.remove());
    probes.clear();
  };
  const handOffBackward = () => {
    const active = document.activeElement as HTMLElement | null;
    if (!active || !container.contains(active)) return;
    const items = focusablesIn(container);
    const index = items.indexOf(active);
    // 最初が日付の欄・動画などのときは、その中の最初の区切りにいるか分からないので任せない
    // （そのときは従来どおり、最初の番兵に来てから最後の操作へ移す）。
    if (index < 0 || isComposite(active)) return;
    // EV-207。前の操作がすべて設定で飛ばされうる種類なら、ここがブラウザの最初の停止先かもしれない。
    // 同じ種類の探り要素を同じ順で最後の番兵の直前に置き、移動をブラウザに任せる。止まる設定なら
    // 探り要素に止まり（focusin で本物へ移す）、止まらない設定なら最後の操作の最後の区切りへ入る。
    // Tab の順・停止先は変えない（tabindex を付けない）。
    const before = items.slice(0, index);
    if (before.some((element) => !maySkipByTabSetting(element))) return;
    removeProbes();
    before.forEach((element) => {
      const probe = createProbe(element);
      probes.set(probe, element);
      // 外側に番兵を置くときは、ダイアログの最後の子にする（最後の番兵の直前になる）。
      if (end.parentNode === container) container.insertBefore(probe, end);
      else container.appendChild(probe);
    });
    handingOff = true;
    try {
      end.focus();
    } finally {
      handingOff = false;
    }
    // ブラウザが動かさなかった（あとの処理が preventDefault した）ときに、見えない番兵に残さない。
    window.clearTimeout(fallback);
    fallback = window.setTimeout(() => {
      removeProbes();
      if (document.activeElement === end) focusEdge(container, 'last');
    }, 0);
  };

  // EV-244。余白・背景のクリック後は、BODY/ダイアログからのTabの起点がブラウザで異なる。
  // 最初の番兵へ起点だけを揃え、その次の実際の操作への移動はブラウザへ任せる。
  // 内部の入力・日付・動画のTabは対象にしない（preventDefaultもしない）。
  const handOffForwardEntry = () => {
    const active = document.activeElement;
    if (active !== document.body && active !== container) return;
    handingOff = true;
    try {
      start.focus();
    } finally {
      handingOff = false;
    }
    window.clearTimeout(fallback);
    fallback = window.setTimeout(() => {
      if (document.activeElement === start) focusEdge(container, 'first');
    }, 0);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (!isActive() || event.defaultPrevented) return;
    // EV-120。変換中の Escape（取り消し）は IME に任せる。
    if (isImeComposing(event)) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      onEscape();
    }
    // Tab は横取りしない（EV-123・EV-124）。端は番兵、外へ出たら focusin で戻す。
    // 最初の操作での Shift+Tab だけ、移動の起点を最後の番兵にする（EV-127。preventDefault しない）。
    if (event.key === 'Tab' && !event.altKey && !event.ctrlKey && !event.metaKey) {
      if (event.shiftKey) handOffBackward();
      else handOffForwardEntry();
    }
  };

  const onFocusIn = (event: FocusEvent) => {
    if (!isActive()) return;
    const target = event.target as Node | null;
    if (!target || target === document) return;
    if (handingOff && (target === start || target === end)) return;
    // EV-207。ブラウザが探り要素に止まった（その種類に止まる設定）なら、対応する本物へ移す。
    // それ以外の移動でも探り要素はすぐ外す。
    const original = probes.get(target as HTMLElement);
    removeProbes();
    if (original) {
      original.focus();
      if (document.activeElement !== original) focusEdge(container, 'last');
      return;
    }
    if (target === start || target === end) {
      // 中の操作から来たときだけ、反対の端の操作へ折り返す（最後の番兵 → 最初、最初の番兵 → 最後）。
      // EV-125。それ以外（BODY＝relatedTarget が null、ダイアログそのもの＝見出し・余白のクリック、
      // 背景の要素）は「入ってきた」として、入ってきた側の端の操作へ（最初の番兵 → 最初）。
      const from = event.relatedTarget as Node | null;
      const entering = from === null || from === container || !container.contains(from);
      if (target === end) focusEdge(container, entering ? 'last' : 'first');
      else focusEdge(container, entering ? 'first' : 'last');
      return;
    }
    // EV-119。フォーカスが背景へ移ったら、ダイアログの中へ戻す。
    if (!container.contains(target)) focusInto(container, false);
  };

  document.addEventListener('keydown', onKeyDown);
  document.addEventListener('focusin', onFocusIn);
  return () => {
    observer?.disconnect();
    window.clearTimeout(fallback);
    removeProbes();
    document.removeEventListener('keydown', onKeyDown);
    document.removeEventListener('focusin', onFocusIn);
    start.remove();
    end.remove();
  };
}
